/**
 * Propertic terminology glossary (2026-02).
 *
 * GPT's site-wide audit flagged inconsistent Arabic terms for:
 *   sale vs procedure · off-plan · yield · mortgage · Golden Visa
 *
 * This module is the ONE canonical reference. Any new component that
 * renders these terms should import from here rather than re-inventing
 * a translation, so EN and AR pages stay aligned as we push updates.
 *
 * Not everything in the codebase reads from this module yet, this is
 * the reference — align new copy against it and refactor legacy strings
 * over time.
 */

export const GLOSSARY = {
  // Transaction vs sale — DLD publishes "procedures" (كل الإجراءات) which
  // includes sales, mortgages, gifts, etc. When we cite "sales" we mean
  // the subset. Keep the distinction visible.
  sale: { en: "sale", ar: "صفقة بيع" },
  sales: { en: "sales", ar: "صفقات بيع" },
  transaction: { en: "transaction", ar: "معاملة" },
  transactions: { en: "transactions", ar: "معاملات" },
  procedure: { en: "procedure", ar: "إجراء" },
  procedures: { en: "procedures", ar: "الإجراءات" },

  // Off-plan / ready — DLD uses "خارطة" for off-plan and "جاهز" for ready.
  offPlan: { en: "off-plan", ar: "خارطة" },
  ready: { en: "ready", ar: "جاهز" },

  // Yield — always qualify as "gross" and disclose the blend.
  grossYield: { en: "Gross yield", ar: "العائد الإجمالي" },
  netYield: { en: "Net yield", ar: "العائد الصافي" },
  yieldMethodologyShort: {
    en: "Gross yield derived by The Propertic from AED/sqft transaction medians that Propertic computes from DLD transaction records and Propertic's own aggregated asking-rent benchmarks. DLD does not publish rental yields directly.",
    ar: "العائد الإجمالي محسوب من قِبل بروبيرتك من وسيطات سعر القدم² التي تحتسبها بروبيرتك من سجلات صفقات دائرة الأراضي مع مؤشرات إيجار الطلب المُجمَّعة داخل بروبيرتك. لا تنشر دائرة الأراضي عوائد الإيجار بشكل مباشر.",
  },

  // Mortgage / EMI — CBUAE terminology.
  mortgage: { en: "Mortgage", ar: "الرهن العقاري" },
  emi: { en: "Monthly EMI", ar: "القسط الشهري" },
  interestRate: { en: "Interest rate", ar: "معدّل الفائدة" },
  ltv: { en: "LTV (Loan-to-Value)", ar: "نسبة التمويل إلى القيمة" },
  mortgageDisclaimer: {
    en: "Illustrative estimate. Not a bank quotation, actual rates and eligibility are set by your lender at approval. Excludes DLD transfer fee (4%), broker fee (2%), trustee & registration fees.",
    ar: "تقدير توضيحي، وليس عرضاً بنكياً. تُحدَّد الأسعار والأهلية من قِبل البنك عند الموافقة. لا تشمل رسوم دائرة الأراضي (٤٪)، وعمولة الوسيط (٢٪)، وأتعاب الأمين ورسوم التسجيل.",
  },

  // Golden Visa — the OFFICIAL wording per ICP + u.ae is "10-year Golden
  // Visa pathway via real estate", conditional on AED 2M+ property value
  // AND ICP/DLD documentation review. NEVER phrase as an automatic outcome.
  goldenVisa: { en: "10-year Golden Visa pathway", ar: "مسار الإقامة الذهبية لعشر سنوات" },
  goldenVisaPathway: {
    en: "10-year Golden Visa pathway (subject to ICP/DLD eligibility)",
    ar: "مسار الإقامة الذهبية لعشر سنوات (وفق شروط الأهلية لدى ICP ودائرة الأراضي)",
  },
  goldenVisaDisclaimer: {
    en: "AED 2M+ property is one eligibility criterion under Cabinet Decision 65/2022, not an automatic approval. Final issuance is subject to the current ICP/DLD checklist.",
    ar: "قيمة العقار من ٢ مليون درهم فأكثر تُعدّ أحد شروط الأهلية وفق قرار مجلس الوزراء ٦٥/٢٠٢٢، وليست موافقة تلقائية. الإصدار النهائي يخضع للقائمة المعتمدة حالياً لدى ICP ودائرة الأراضي.",
  },

  // Source hierarchy — three buckets Roni / the site use consistently.
  sourceDLD: { en: "DLD-published (raw)", ar: "منشور من دائرة الأراضي (خام)" },
  sourcePropertic: { en: "Propertic-calculated (from DLD raw)", ar: "محسوب من Propertic (استناداً إلى بيانات دائرة الأراضي)" },
  sourceThirdParty: { en: "Third-party rental data", ar: "بيانات إيجار من مصادر خارجية" },
};

/** Convenience helper: pick either `en` or `ar` from a glossary node. */
export const g = (key, lang = "en") => (GLOSSARY[key]?.[lang === "ar" ? "ar" : "en"] ?? "");
